import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home,
  Calendar,
  Users,
  Clock,
  FileText,
  Settings,
  LogOut,
  User,
  Shield,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isDoctor, isDispatcher, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', icon: Calendar, label: 'Календарь' },
    { path: '/dashboard', icon: Home, label: 'Главная' },
    ...(isDispatcher() ? [
      { path: '/patients', icon: Users, label: 'Пациенты' },
      { path: '/appointments', icon: Clock, label: 'Записи' }
    ] : []),
    ...(isDoctor() ? [
      { path: '/schedule', icon: Settings, label: 'Расписание' }
    ] : []),
    { path: '/history', icon: FileText, label: 'История' },
    ...(isAdmin() ? [
      { path: '/users', icon: Shield, label: 'Пользователи' }
    ] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavLinks = ({ onClose }) => (
    <div className="space-y-1 px-3">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={`group flex items-center px-4 py-3.5 text-base font-medium rounded-xl transition-all duration-150 ${
              isActive
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-blue-50 hover:text-blue-700'
            }`}
          >
            <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-blue-600'}`} />
            <span>{item.label}</span>
            {isActive && <ChevronRight className="ml-auto h-4 w-4 text-blue-200" />}
          </Link>
        );
      })}
    </div>
  );

  const roleLabel = isDoctor() ? 'Врач' : isAdmin() ? 'Администратор' : 'Диспетчер';
  const roleColor = isDoctor() ? 'bg-green-100 text-green-800' : isAdmin() ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Шапка */}
      <header className="bg-blue-800 shadow-lg sticky top-0 z-30">
        <div className="px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2.5 rounded-lg text-blue-200 hover:text-white hover:bg-blue-700 transition-colors"
                aria-label="Открыть меню"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <div className="flex items-center space-x-2.5">
                <div className="bg-blue-600 rounded-lg p-1.5">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <span className="text-white font-bold text-lg tracking-tight">
                  Записи к врачу
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="hidden sm:flex items-center space-x-2 bg-blue-700 rounded-xl px-3 py-2">
                <div className="bg-blue-500 rounded-full p-1">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white text-sm font-semibold leading-tight">{user?.name}</p>
                  <p className="text-blue-300 text-xs leading-tight">{roleLabel}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1.5 bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded-xl transition-colors text-sm font-medium"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Выйти</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Мобильное меню */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-black bg-opacity-50"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="fixed left-0 top-16 bottom-0 w-72 bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pt-4 pb-6">
              <div className="px-4 mb-4">
                <div className="bg-slate-50 rounded-xl p-3 flex items-center space-x-3 border border-slate-200">
                  <div className="bg-blue-600 rounded-full p-2">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-semibold text-base">{user?.name}</p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor}`}>{roleLabel}</span>
                  </div>
                </div>
              </div>
              <NavLinks onClose={() => setMobileMenuOpen(false)} />
              <div className="px-4 mt-4">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-700 rounded-xl transition-colors font-medium text-base"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Выйти из системы</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Боковое меню — десктоп */}
        <aside className="hidden md:flex flex-col w-72 bg-white shadow-sm flex-shrink-0 border-r border-slate-200" style={{ minHeight: 'calc(100vh - 4rem)' }}>
          <div className="p-4 border-b border-slate-100">
            <div className="bg-slate-50 rounded-xl p-3 flex items-center space-x-3">
              <div className="bg-blue-600 rounded-full p-2 flex-shrink-0">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-slate-900 font-semibold text-base truncate">{user?.name}</p>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor}`}>{roleLabel}</span>
              </div>
            </div>
          </div>

          <nav className="flex-1 py-4 overflow-y-auto">
            <p className="px-7 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Меню</p>
            <NavLinks onClose={() => {}} />
          </nav>

          <div className="p-4 border-t border-slate-100">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-700 rounded-xl transition-colors font-medium text-base"
            >
              <LogOut className="h-5 w-5" />
              <span>Выйти из системы</span>
            </button>
          </div>
        </aside>

        {/* Основной контент */}
        <main className="flex-1 p-4 md:p-6 min-w-0 overflow-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
